import { useCallback, useContext, useEffect, useState } from "react";
import Dropdown from "../../components/dropdown/dropdown";
import { BannerState, PBanner, PButton, PSpinner, PText } from "@porsche-design-system/components-react";
import { CustomTable } from "../../components/customTable/customTable";
import { CustomTableHeader } from "../../components/customTable/customTableHeader";
import {
	cancelReservation,
	exportMyNumbers,
	getLimitationNumbersByOrderTypeDealer,
	getLimitationNumbersByOrderTypeImporter,
	saveDealerLimitationNumber
} from "../../api/LimitationNumberService";
import { LimitationNumber, SaveDealerReservationRequest, State } from "../../store/limitationNumber";
import { getQuoteByOrderTypeAndPartnerNumber } from "../../api/ModelImporterQuoteService";
import UserContext, {UserContextType} from "../../store/UserContext";
import { showDateTimeAsString } from "../../utils";
import { getDealersByImporter } from "../../api/DealerService";
import { QuotaInformation } from "./QuotaInformation";
import { Dealer } from "../../store/types";
import { useModelState } from "../../store/ModelContext";
import { Grid } from "@react-css/grid";

type ModelBanner = {
	state: BannerState;
	title: string;
	description: string;
	duration: number;
};

interface ControlProps {
	triggerUpdate: boolean;
	activeTab : number;
}

export function MyNumbersTab(props : ControlProps) {

	const { partnerNumber, lenaAuthToken , isDealer} = useContext<UserContextType>(UserContext);
	const modelContextState = useModelState();
	const [loading, setLoading] = useState(false);
	const tableId = "myReservationsTable";
	const [limitationNumbers, setLimitationNumbers] = useState<Array<LimitationNumber>>([]);
	const [isBannerShow, handleBannerShow] = useState(false);
	const [isDeleteBanner, handleCancelBannerShow] = useState(false);	
	const [reservationToCancelId, setReservationToCancelId] = useState(null);
	const [reservationToCancelNumber, setReservationToCancelNumber] = useState(null);
	const [ordersByChanceCount, setOrdersByChanceCount] = useState<number>();
	const [overAllQuote, setOverAllQuote] = useState<number>(null);
	const [assignedQuotes, setAssignedQuotes] = useState<number>(null);
	const [dealerList, setDealerList] = useState<Array<Dealer>>([]);


	const initialBanner: ModelBanner = {
		state: "info",
		title: "",
		description: "",
		duration: 6000
	};
	const [bannerInfo, handleBannerInfo] = useState(initialBanner);

	useEffect( () => {
		let dataList: Dealer[] = [];
		if(!isDealer){
			getDealersByImporter(lenaAuthToken, partnerNumber)
			.then(((dealers) => {
				dealers.forEach((dealer) => {
					dealer.toString = function (): string {
						return `${this.partnerNumber} - ${this.displayName}`;
					};
					dataList[dealer.partnerNumber]=dealer;
				})
			}))
			setDealerList(dataList);
		}
	}, [lenaAuthToken, partnerNumber, isDealer]);

	const callGetImporterQuoteByOrderTypeAndImporter = useCallback(() => {
		getQuoteByOrderTypeAndPartnerNumber(lenaAuthToken, modelContextState.globalOrderType, partnerNumber)
		.then((quote) => {
			setOverAllQuote(quote);
		})
		.catch((error) => {
			setOverAllQuote(null);
			console.log(error.message);
		});
	}, [modelContextState.globalOrderType, partnerNumber, lenaAuthToken]);

	const handleReservationResponse = useCallback((response: LimitationNumber[], partnerNumber) => {
		response = response.filter((item) => (item.reservationState === State.RESERVED || item.reservationState === State.BOOKED));
		setAssignedQuotes(response.filter((item) => item.limitedNumber > 0).length);

		let limitationNumbersWithoutByChance = response.filter((item) => item.limitedNumber !== 0);
		let limitationNumbersWithoutByChanceWithMappedDealer = limitationNumbersWithoutByChance.map(value => {
			if (value.dealerNumber != null && dealerList.length > 0) {
				let dealerFound  = dealerList.filter(item => item.partnerNumber === value.dealerNumber );
				if (dealerFound.length === 0) {
					let dealer: Dealer = {
						oid: "",
						displayName: "Nicht verfügbar",
						importer: "",
						partnerNumber: value.dealerNumber
					};
					dealer.toString = function (): string {
						return `${this.partnerNumber} - ${this.displayName}`;
					};
					dealerList[value.dealerNumber] = dealer;
				}
			}
			return value;
		});
		setLimitationNumbers(limitationNumbersWithoutByChanceWithMappedDealer);

		let countOrdersByChance = response.filter(
				(reservation) =>
						reservation.reservationState === State.BOOKED &&
					((!isDealer && reservation.importer === partnerNumber) || (isDealer && reservation.dealerNumber === partnerNumber)) &&
						reservation.limitedNumber === 0
		).length;
		setOrdersByChanceCount(countOrdersByChance);
	}, [dealerList, isDealer]);

	const loadReservations = useCallback(() => {
		setLimitationNumbers([]);
		if (modelContextState.globalOrderType !== "" && props.triggerUpdate && props.activeTab === 0) {
			if(isDealer){
				getLimitationNumbersByOrderTypeDealer(lenaAuthToken, modelContextState.globalOrderType, partnerNumber)
					.then((response) => {
						handleReservationResponse(response, partnerNumber);
					})
					.then(() => {
						callGetImporterQuoteByOrderTypeAndImporter();
					})
					.then(() => {
						setLoading(false);
					})
					.catch((error) => {
						console.log(error.message);
					});
				
			}else{
				getLimitationNumbersByOrderTypeImporter(lenaAuthToken, modelContextState.globalOrderType, partnerNumber)
				.then((response) => {
					handleReservationResponse(response, partnerNumber);
				})
				.then(() => {
					callGetImporterQuoteByOrderTypeAndImporter();
				})
				.then(() => {
					setLoading(false);
				})
				.catch((error) => {
					console.log(error);
				})
			}
			
			setLoading(true);
		}
	}, [modelContextState.globalOrderType, props.activeTab, props.triggerUpdate, partnerNumber, lenaAuthToken, isDealer, callGetImporterQuoteByOrderTypeAndImporter, handleReservationResponse]);


	/**
	 * Load limitation numbers
	 */
	useEffect(() => {
		loadReservations();
	}, [partnerNumber, loadReservations]);


	const showBanner = (message : string, resultState: BannerState) => {
		let bannerInfo: ModelBanner = {
			state: resultState,
			title: "LENA Notification",
			description: message,
			duration: 6000
		};
		handleBannerInfo(bannerInfo);
		handleBannerShow(true);
		setTimeout(() => {
			handleBannerShow(false);
		}, bannerInfo.duration);
	};


	function handleCancelReservation(reservation: string, selectedReservation: number) {
		handleCancelBannerShow(false);
		setReservationToCancelId(reservation);
		setReservationToCancelNumber(selectedReservation)
		setTimeout(() => {
			handleCancelBannerShow(true);
		}, 100);
	}	

	function handleCancelReservationConfirm() {
		if (reservationToCancelId === null) return;
		cancelReservation(lenaAuthToken, reservationToCancelId)
			.then((result) => {
				setReservationToCancelId(null);
				handleCancelBannerShow(false);
				showBanner(result, "info");
				loadReservations();
			})
			.catch((error) => {
				console.log(error.message);
			});
	}

	function handleCancelReservationCancel() {	
		handleCancelBannerShow(false);
	}

	function onChangeDealer(value : string, reservation: LimitationNumber) {
		console.log("onChange");
		let saveDealerReservationRequest: SaveDealerReservationRequest = {
			importer: reservation.importer,
			reservationState: reservation.reservationState,
			orderType: reservation.orderType,
			dealer: value ? value : "",
			limitedNumber: reservation.limitedNumber
		};
		saveDealerLimitationNumber(lenaAuthToken, saveDealerReservationRequest)
			.then(() => {
				let reservationToUpdate = limitationNumbers.find((item) => item.limitedNumber === reservation.limitedNumber);
				reservationToUpdate.dealerNumber = value;
				console.log(limitationNumbers);
			})
			.catch((error) => console.log(error));
	}

	function onExport() {
		exportMyNumbers(lenaAuthToken, modelContextState.globalOrderType, partnerNumber)
			.then((response) => response.blob())
			.then((blob) => {
				let url = window.URL.createObjectURL(blob);
				let a = document.createElement("a");
				a.href = url;
				a.download = modelContextState.globalOrderType + "_MyNumbers.csv";
				document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
				a.click();
				a.remove(); //afterward we remove the element again
			})
			.catch((error) => console.log(error));
	}

	return (
		<div className="wrapper">
			{isBannerShow && (
				<PBanner  state={bannerInfo.state} dismissButton={false} className="create-model__banner_position" autoFocus={false} inert={true} open heading={bannerInfo.title}>
					<span slot="description">{bannerInfo.description}</span>
				</PBanner>
			)}
			{isDeleteBanner && (
				<PBanner
					dismissButton={true}
					onDismiss={() => handleCancelReservationCancel()}
					className="create-model__banner_position delete-confirm-banner"  autoFocus={false} inert={false} open heading={"Do you want to cancel reservation " + reservationToCancelNumber + "?"} >
					<div slot="description" className="delete-confirm-banner__description">
						<PButton variant="secondary" className="confirm-button" onClick={() => handleCancelReservationCancel()}  autoFocus={false} inert={false}>
							Cancel
						</PButton>
						<PButton variant="primary" className="confirm-button" onClick={() => handleCancelReservationConfirm()}  autoFocus={false} inert={false}>
							Ok
						</PButton>
					</div>
				</PBanner>
			)}
			<div>
					{loading && <PSpinner className={"spinner"} size={{ base: "small", l: "medium" }}  autoFocus={false} inert={true} />}
					{!loading && (
						<div>
							<Grid  autoFlowColumn={true} columns='[first] 50% [line2] 50%'>
								<Grid.Item >
									{assignedQuotes !== null && overAllQuote !== null && (
										<QuotaInformation assignedQuotes={assignedQuotes} overAllQuote={overAllQuote} />
									)}
									{assignedQuotes !== undefined && ordersByChanceCount !== undefined && (
										<PText weight={"bold"}  autoFocus={false} inert={true}>Orders by chance: {ordersByChanceCount}</PText>
									)}
								</Grid.Item>
								<Grid.Item>
									{modelContextState.globalOrderType !== "" && (
										<PButton className="export-button" variant={"primary"} onClick={() => onExport()}  autoFocus={false} inert={false}>
											Export
										</PButton>
									)}
								</Grid.Item>
							</Grid>
							<CustomTable tableId={tableId} className={"my-numbers-table"}>
								<thead>
									<tr>
										<CustomTableHeader text={"No."} tableId={tableId} columnPos={0} sortable={true} />
										<CustomTableHeader text={"Reservation End"} tableId={tableId} columnPos={1} sortable={true} isDate={true}/>
										<CustomTableHeader text={"State"} tableId={tableId} columnPos={2} sortable={true} />
										<CustomTableHeader text={"Order"} tableId={tableId} columnPos={3} sortable={true} />
										{!isDealer && <CustomTableHeader text={"Dealer"} tableId={tableId} columnPos={4} sortable={true} />}
										<CustomTableHeader text={""} tableId={tableId} columnPos={5} sortable={false} />
									</tr>
								</thead>
								{limitationNumbers.length > 0 &&
								<tbody>
									{Array.from(limitationNumbers).map((reservation) => (
										<tr key={reservation.limitedNumber}>
											<td>{reservation.limitedNumber}</td>
											<td>{reservation.reservationState === State.RESERVED && showDateTimeAsString(reservation.endReservedDateTime)}</td>
											<td>{reservation.reservationState}</td>
											<td>{reservation.commissionNumber}</td>
											{!isDealer && reservation.reservationState === State.RESERVED && (
												<td width={"30%"}>
													<Dropdown
														placeholder="Dealer"
														value={reservation.dealerNumber}
														onChange={(value) => {
															if (reservation.dealerNumber !== value && reservation.orderType === modelContextState.globalOrderType) {
																onChangeDealer(value, reservation);
															}
														}}
														data={dealerList}
														multipleSelections={false}
														clearable={reservation.reservationState === State.RESERVED}
														searchable={true}
														showTooltip={false}
														label={"Dealer"}
													/>
												</td>
											)}
											{!isDealer && reservation.reservationState === State.BOOKED && <td width={"30%"}>{reservation.dealerNumber}</td>}
											<td>
												{reservation.reservationState === State.RESERVED && (
													<PButton variant="secondary" onClick={() => handleCancelReservation(reservation.id, reservation.limitedNumber)}  autoFocus={false} inert={false}>
														Cancel
													</PButton>
												)}
											</td>
										</tr>
									))}
								</tbody>
									}
							</CustomTable>
						</div>
					)}
			</div>
		</div>
	);
}
